import React, { useCallback, useEffect, useMemo, useState } from "react";

const DECLARATION_TYPES = ["", "First", "Biennial", "Final"];

export default function NotSubmittedUsersModule({ endpointBase = "/api/admin", title = "Users Not Submitted" }) {
  const token = localStorage.getItem("adminToken");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    department: "",
    subDepartment: "",
    declarationType: "",
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (filters.search) params.set("search", filters.search);
    if (filters.department) params.set("department", filters.department);
    if (filters.subDepartment) params.set("subDepartment", filters.subDepartment);
    if (filters.declarationType) params.set("declarationType", filters.declarationType);
    return params.toString();
  }, [page, limit, filters]);

  const fetchRows = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${endpointBase}/users/not-submitted?${queryString}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to fetch users not submitted");
      }
      setRows(Array.isArray(data.data) ? data.data : []);
      setTotal(data.pagination?.total || 0);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setTotalPages(1);
      setError(e.message || "Failed to fetch users not submitted");
    } finally {
      setLoading(false);
    }
  }, [endpointBase, token, queryString]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.department) params.set("department", filters.department);
    if (filters.subDepartment) params.set("subDepartment", filters.subDepartment);
    if (filters.declarationType) params.set("declarationType", filters.declarationType);

    const url = `${endpointBase}/users/not-submitted/export/csv?${params.toString()}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          let msg = "Export failed";
          try {
            const data = await res.json();
            msg = data.message || msg;
          } catch {
            // keep fallback
          }
          throw new Error(msg);
        }
        return res.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "users_not_submitted.csv";
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch((e) => {
        setError(e.message || "Export failed");
      });
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
        <h3 className="card-title mb-0">{title}</h3>
        <button className="btn btn-light btn-sm" onClick={downloadCsv}>
          Export CSV
        </button>
      </div>

      <div className="card-body">
        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <input
              className="form-control form-control-sm"
              placeholder="Search name, payroll, ID, email"
              value={filters.search}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, search: e.target.value }));
              }}
            />
          </div>
          <div className="col-md-3">
            <input
              className="form-control form-control-sm"
              placeholder="Department"
              value={filters.department}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, department: e.target.value }));
              }}
            />
          </div>
          <div className="col-md-3">
            <input
              className="form-control form-control-sm"
              placeholder="Sub Department"
              value={filters.subDepartment}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, subDepartment: e.target.value }));
              }}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select form-select-sm"
              value={filters.declarationType}
              onChange={(e) => {
                setPage(1);
                setFilters((f) => ({ ...f, declarationType: e.target.value }));
              }}
            >
              {DECLARATION_TYPES.map((type) => (
                <option key={type || "any"} value={type}>
                  {type || "Any Type"}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="alert alert-danger py-2">{error}</div>}

        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="small text-muted">Total: {total}</div>
              <div className="d-flex align-items-center gap-2">
                <label className="small text-muted mb-0">Rows</label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 90 }}
                  value={limit}
                  onChange={(e) => {
                    setPage(1);
                    setLimit(parseInt(e.target.value, 10) || 50);
                  }}
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Payroll</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>National ID</th>
                    <th>Department</th>
                    <th>Sub Department</th>
                    <th>Designation</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-3">
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((u) => (
                      <tr key={u.id}>
                        <td>{u.payroll_number || ""}</td>
                        <td>{`${u.first_name || ""} ${u.other_names || ""} ${u.surname || ""}`.replace(/\s+/g, " ").trim()}</td>
                        <td>{u.email || ""}</td>
                        <td>{u.national_id || ""}</td>
                        <td>{u.department || ""}</td>
                        <td>{u.sub_department || ""}</td>
                        <td>{u.designation || ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end align-items-center gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>
              <span className="small text-muted">Page {page} of {totalPages}</span>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
